import { ID, IsoDate, IsoDateTime, JsonArray, JsonObject, LocalizedJsonObject } from 'src/models/model'
import { inject } from 'vue'

/** Used for converting the `valueType` prop value to the appropriate class. */
const VALUE_TYPE_OVERRIDE_NAME_TO_CLASS_MAP = {
  Boolean,
  Number,
  Integer: Number,
  Int: Number,
  ID,
  String,
  IsoDate,
  IsoDateTime,
  JsonObject,
  LocalizedJsonObject,
  Object: JsonObject,
  JsonArray,
  Array: JsonArray
}

/**
 * This mixin is used to create form components that automatically detect and react to the nearest parent
 * ModelForm, as well as the current model it is editing. The form component does not need to be passed in
 * the parent form or model being edited.
 *
 * This allows form fields to change when a model's language is changed for translations editing,
 * and makes it possible to dynamically configure form components based on the model field's Type class.
 *
 * There are some assumptions that must be met:
 * - The component must be a descendant sub-component of ModelForm. It doesn't need to be a direct child.
 * - The component must be given a `field` prop that is the name or property path string to the model field
 *   it edits.
 * - The `field` must be defined on the model. It cannot be an ad-hoc property on the model object.
 * - The component defaults to editing the closest parent ModelForm's model. This can be overridden by passing
 *   a `model` prop to the component.
 * - For nested JSON `field`s, the `valueType` prop is required to determine how to parse the input value.
 */
export default {
  // Disabling this lets us manually handle forwarding $attrs as props. $attrs always includes
  // anything given to a component that is not defined as a prop. It's usually for HTML attributes
  // but it will still receive JS values the same way props do.
  inheritAttrs: false,
  // Note that any attributes defined as props will exclude them from the $attrs object, so they
  // must be manually passed to the input component.
  props: {
    autofocus: {
      type: Boolean,
      default: false
    },
    disable: {
      type: Boolean,
      default: false
    },
    hint: {
      type: String,
      required: false,
      default: ''
    },
    placeholder: {
      type: [String, Number],
      required: false,
      default: ''
    },
    readonly: {
      type: Boolean,
      default: false
    },
    rules: {
      type: Array,
      required: false,
      default: null
    },
    // The name of the model field. Can be a path string, e.g. "field.subFields[1].subSubField"
    field: {
      type: String,
      required: true
    },
    hideWhenTranslating: {
      type: Boolean,
      default: false
    },
    model: {
      type: Object,
      required: false,
      default: null
    },
    // This is mainly used for nested JSON key values, so we know what data type to parse the
    // input component's emitted value as. Must be a type supported by Models. For non-JSON fields
    // this is not usually needed because the type can be inferred from the field configuration.
    // Example prop values:
    // - string: `"Number"`, or `"[Number]"` array for collections
    // - class: `Number`, or `[Number]` array for collections
    valueType: {
      type: [String, Function, Array],
      required: false,
      default: null
    },
    // Optional function called with the value read from the model. The returned value is
    // what is passed to the input component as its value.
    decode: {
      type: Function,
      required: false,
      default: null
    },
    // Optional function called with the value that has been parsed from the input component.
    // The returned value is what is set on the model.
    encode: {
      type: Function,
      required: false,
      default: null
    }
  },
  data () {
    return {
      parentModelForm: null,
      parentModel: null
    }
  },
  emits: ['update:model-value'],
  computed: {
    computedAutofocus () {
      return this.autofocus && !this.$q.platform.is.ios
    },
    computeDefaultValueFn () {
      const { field } = this.fieldType

      // For JSON fields, get the default from the parent field's default object
      if (this.isJsonField && this.field.includes('.')) {
        const pathSteps = this.field.split('.')
        const parentFieldName = pathSteps[0]
        const nestedPath = pathSteps.slice(1).join('.')

        const parentFieldType = this.computedModel?.fieldType(parentFieldName)
        if (parentFieldType && parentFieldType.default) {
          const parentDefault = _.isFunction(parentFieldType.default)
            ? parentFieldType.default()
            : parentFieldType.default
          return () => _.get(parentDefault, nestedPath)
        }
      }

      // For regular fields, use the field's default
      if (field) {
        if (_.isFunction(field.default)) {
          return field.default
        } else if (_.has(field, 'default')) {
          return () => field.default
        }
      }

      return () => null
    },
    computedDisable () {
      return this.isEditingTranslationAndFieldNotTranslatable || this.isFieldValueMaskedByApi || this.disable
    },
    /**
     * For JSON field (paths), this handles renaming the field key to the appropriate language key for
     * JSON translation editing. For non-JSON fields, this returns null.
     * @returns {string | null} - The field prop or a localized JSON key path
     */
    localizedJsonFieldKey () {
      const { field: fieldConfig } = this.fieldType
      if (!fieldConfig) return null

      const jsonKeyPathSteps = _.split(this.field, '.')
      const maybeTranslatableKey = jsonKeyPathSteps.pop()
      const [localizedKey, baseKey, wasLocalized] = LocalizedJsonObject.localizeKey(maybeTranslatableKey, this.currentLanguage)
      if (wasLocalized && _.includes(fieldConfig.translatables, baseKey)) {
        jsonKeyPathSteps.push(localizedKey)
        return jsonKeyPathSteps.join('.')
      } else {
        return null
      }
    },
    computedHint () {
      if (this.isEditingTranslationAndFieldTranslatable && !this.fieldValue) {
        const languageName = this.$i18n.t(`languages.${this.currentLanguage}`)
        return `${languageName} value is blank. English will be used.`
      } else {
        return this.hint
      }
    },
    computedModel () {
      return this.model || this.parentModel || this.parentModelForm?.$options?.propsData?.model || null
    },
    computedPlaceholder () {
      return this.englishFallbackValue || this.placeholder
    },
    computedReadonly () {
      return this.isEditingTranslationAndFieldNotTranslatable || this.isFieldValueMaskedByApi || this.readonly
    },
    computedRules () {
      return this.computedModel?.isTranslation ? undefined : this.rules
    },
    computedSlots () {
      const slots = _.keys(this.$slots)

      if (this.isEditingTranslationAndFieldNotTranslatable) {
        return _.omit(slots, 'default')
      } else {
        return slots
      }
    },
    currentLanguage () {
      // This can execute before parentModelForm is set
      return this.computedModel?.language || this.parentModelForm?.currentLanguage
    },
    englishFallbackValue () {
      if (!this.isEditingTranslationAndFieldTranslatable) return null

      if (this.isJsonField) {
        // JSON columns hold their own translations, unlike other attributes which are handled by Mobility DB tables.
        // LocalizedJsonObjects proxy their keys to their current language keys, so we force fetch the English value
        return _.get(this.computedModel, this.field) || null
      } else if (this.computedModel?.englishFallbackValues) {
        // `englishFallbackValues` is an extra nullable JSON field returned by translatable models only.
        // Its keys are not camelCased.
        return _.get(this.computedModel.englishFallbackValues, _.snakeCase(this.field)) || null
      } else {
        return null
      }
    },
    isEditingTranslationModel () {
      return this.computedModel?.isTranslationEditingAllowed && this.computedModel?.isTranslation
    },
    isEditingTranslationAndFieldNotTranslatable () {
      return this.isEditingTranslationModel && !this.isFieldTranslatable
    },
    isEditingTranslationAndFieldTranslatable () {
      return this.isEditingTranslationModel && this.isFieldTranslatable
    },
    isFieldTranslatable () {
      const { field, isTranslatableField } = this.fieldType
      if (this.isJsonField) {
        // JSON columns hold their own translations, unlike other attributes which are handled by Mobility DB tables.
        // Models can declare which specific JSON sub keys should be translatable.
        const englishBaseKey = _.last(this.field.split('.'))
        return isTranslatableField && _.includes(field.translatables, englishBaseKey)
      } else {
        return isTranslatableField
      }
    },
    isFieldValueMaskedByApi () {
      return this.computedModel && this.computedModel.isFieldMaskedByApi(this.field)
    },
    isJsonField () {
      const typeName = this.fieldType?.Type?.name
      return typeName === 'JsonObject' || typeName === 'JsonArray' || typeName === 'LocalizedJsonObject'
    },
    fieldValue () {
      const field = this.localizedJsonFieldKey || this.field
      let value = _.get(this.computedModel, field)
      if (_.isFunction(this.decode)) {
        value = this.decode(value)
      }

      if (this.englishFallbackValue && value === this.englishFallbackValue) {
        return null
      } else {
        return value
      }
    },
    /**
     * To support nested `field` paths like `"field.subField.subSubField..."`, we drill
     * down the path to find the innermost field type. Field paths are typically used
     * for JSON keys, or subModel.data paths, and in those cases the last field(s) in the
     * path may not correspond to a model Type, as they may be nested inside a model type.
     * @returns {object} - A fieldType object
     */
    fieldType () {
      if (this.readonly) {
        return { Type: String, default: null }
      } else {
        this.validateFieldProp()
      }

      const pathSteps = this.field.split('.')
      let model = this.computedModel
      let fieldType
      while (pathSteps.length > 0 && _.isFunction(model?.fieldType)) {
        const currentPathStep = pathSteps.shift()
        if (currentPathStep === 'data') {
          // We know model is a Model because it responds to `fieldType`. We commonly access a
          // sub-model's `data` object, and in this case we can treat the object as the model itself.
          continue
        }
        fieldType = model.fieldType(currentPathStep)
        model = fieldType.Type
      }
      return fieldType
    },
    /**
     * Returns either null or a field object similar to Model.fieldType()
     * @returns {object | null} - { isCollection: boolean, Type: class }
     */
    valueTypeOverride () {
      let valueType = this.valueType
      if (!valueType) return null

      if (_.isString(valueType) && _.startsWith(valueType, '[') && _.endsWith(valueType, ']')) {
        const maybeTypeClass = VALUE_TYPE_OVERRIDE_NAME_TO_CLASS_MAP[valueType.slice(1, -1)] // brackets removed
        if (maybeTypeClass) {
          valueType = [maybeTypeClass]
        } else {
          try {
            valueType = JSON.parse(valueType)
          } catch (e) {
            throw new Error(`<${this.$options.name} model="${this.computedModel.typeName}" field="${this.field}" /> 'valueType' prop is invalid JSON string: '${valueType}'`)
          }
        }
      }

      let isCollection = false
      if (_.isArray(valueType)) {
        valueType = valueType[0]
        isCollection = true
      }

      let Type
      if (_.isString(valueType) && _.has(VALUE_TYPE_OVERRIDE_NAME_TO_CLASS_MAP, valueType)) {
        Type = VALUE_TYPE_OVERRIDE_NAME_TO_CLASS_MAP[valueType]
      } else if (valueType.name && _.has(VALUE_TYPE_OVERRIDE_NAME_TO_CLASS_MAP, valueType.name)) {
        Type = VALUE_TYPE_OVERRIDE_NAME_TO_CLASS_MAP[valueType.name]
      } else if (_.isFunction(valueType) && _.includes(VALUE_TYPE_OVERRIDE_NAME_TO_CLASS_MAP, valueType)) {
        // Was given a class; JS classes are functions
        Type = valueType
      } else {
        throw new Error(`<${this.$options.name} model="${this.computedModel.typeName}" field="${this.field}" /> 'valueType' prop is invalid: '${valueType}'`)
      }
      return { isCollection, Type }
    }
  },
  watch: {
    currentLanguage () {
      const inputComponent = this.$refs.inputComponent
      if (inputComponent && !!inputComponent.resetValidation) {
        inputComponent.resetValidation()
      }
    },
    'fieldType.Type': {
      immediate: true,
      handler () {
        if (this.isJsonField) {
          // When modifying a nested JSON field, we need to know what class to parse and set the value with.
          // Some Quasar form components already return booleans (e.g. checkboxes) or arrays (e.g. multi-select)
          // so they may work out of the box. However regular Input components usually return strings that
          // may not be JSON parseable.
          const fieldIsSubPath = _.includes(this.field, '.')
          const valueTypeIsMissing = !this.valueType
          if (fieldIsSubPath && valueTypeIsMissing) {
            throw new Error(`<${this.$options.name} model="${this.computedModel.typeName}" field="${this.field}" /> 'valueType' prop is required for JSON field path`)
          }
        }
      }
    }
  },
  created () {
    this.parentModel = inject('model')
    this.parentModelForm = this.$findClosestParentByName('ModelForm')
    if (!this.parentModelForm) {
      throw new Error(`The ${this.$options.name} component must be a descendant of a ModelForm component.`)
    }
  },
  methods: {
    handleInput (value) {
      this.setFieldValue(value)
      this.$emit('update:model-value', this.fieldValue)
    },
    parseInputValue (value) {
      if (value && _.isString(value)) {
        value = value.trim()
      }

      const { isCollection, Type } = (this.valueTypeOverride || this.fieldType)
      if (isCollection) {
        if (value === null || value === '') {
          value = []
        } else if (_.isString(value)) {
          try {
            value = JSON.parse(value)
          } catch (e) {
            throw new Error(`<${this.$options.name} model="${this.computedModel.typeName}" field="${this.field}" /> value could not be parsed into array for collection field`)
          }
        }

        if (!_.isArray(value)) {
          throw new Error(`<${this.$options.name} model="${this.computedModel.typeName}" field="${this.field}" /> 'value' prop must be an array for collection field`)
        }
        return _.map(value, arrayElement => this.parseValueUsingType(arrayElement, Type))
      } else {
        return this.parseValueUsingType(value, Type)
      }
    },
    parseValueUsingType (value, Type) {
      if (Type.name === 'IsoDate') {
        // Comparing Date objects equality is not straightforward and can
        // lead to infinite setValue -> handleInput loops. We delegate
        // date handling to the IsoDateInput component.
        return value
      } else if (Type.name === 'IsoDateTime') {
        throw new Error(`<${this.$options.name} model="${this.computedModel.typeName}" field="${this.field}" /> IsoDateTime is not yet supported, requires an IsoDateTimeInput component`)
      } else if (Type === Boolean) {
        return value === true || value === 'true'
      } else if (Type === Number) {
        const result = parseFloat(value)
        if (isNaN(result)) {
          const defaultFunction = this.computeDefaultValueFn()
          return _.isFunction(defaultFunction) ? defaultFunction() : null
        }
        return result
      } else if (_.isFunction(Type.parse)) {
        return Type.parse(value)
      } else if (Type === String) {
        value = String(value)
        if (value === 'null' || value === 'undefined') {
          value = null
        }
        return value
      } else if (value) {
        return value
      } else {
        return this.computeDefaultValueFn()
      }
    },
    setFieldValue (value) {
      if (this.readonly || this.disable) return

      const field = this.localizedJsonFieldKey || this.field
      value = this.parseInputValue(value)
      if (this.englishFallbackValue && value === this.englishFallbackValue) {
        // We should avoid setting a translations to the same value as their English values
        value = null
      }
      if (_.isFunction(this.encode)) {
        value = this.encode(value)
      }
      _.set(this.computedModel, field, value)
      // Uncomment for helpful debugging logs:
      // console.log(`<${this.$options.name} model="${this.computedModel.typeName}" field="${field}" /> set value as:`, this.fieldValue, `(${typeof value})`)
    },
    validateFieldProp () {
      if (!this.computedModel) {
        // Model not yet determined
        return true
      } else if (this.readonly) {
        // Allow read-only from some non-field property
        return true
      }

      // If the field is a path, we only check the first part of the path for simplicity
      const fieldPathStart = this.field.split('.')[0]
      if (this.computedModel.hasField(fieldPathStart)) {
        return true
      } else {
        throw new Error(`<${this.$options.name} model="${this.computedModel.typeName}" field="${this.field}" /> field does not exist`)
      }
    }
  }
}
